import classNames from 'classnames';
import useWorldTheme from 'hooks/shared/useWorldTheme';
import { useHighlight } from 'hooks/useHighlight';
import type { FC, JSX } from 'react';
import { forwardRef } from 'react';
import { getTracingEvents } from 'utils/amplitude';
import { Loader } from '../Loader';
import type { ButtonComponentProps } from './Button.types';

const theme = {
  structure: {
    // min-w-[5rem]
    primary:
      'shadow-sm aria-disabled:!bg-primary-50 aria-disabled:!text-primary-200 focus:outline-none focus-visible:ring-2 border border-transparent',
    // min-w-[5rem]
    secondary:
      'bg-white aria-disabled:!bg-primary-50 aria-disabled:!text-primary-200 border border-gray-300 shadow-sm focus:outline-none focus-visible:ring-2',
    light:
      'shadow-sm aria-disabled:!bg-primary-50 aria-disabled:!text-primary-200 focus:outline-none focus-visible:ring-2 border border-transparent',
    transparent:
      'aria-disabled:!bg-primary-50 aria-disabled:!text-primary-200 focus:outline-none focus-visible:ring-2 border border-transparent',
    text: 'focus:outline-none focus-visible:ring-2 aria-disabled:!text-primary-400 focus-visible:ring-primary-400',
  },
  colors: {
    primary: {
      bank: 'bg-bank hover:bg-bank-800 focus-visible:ring-bank-100 text-white hover:text-white',
      expenses:
        'bg-expenses hover:bg-expenses-800 focus-visible:ring-expenses-100 text-white hover:text-white',
      primary:
        'bg-primary hover:bg-primary-800 focus-visible:ring-primary-100 text-white hover:text-white',
      taxes:
        'bg-taxes hover:bg-taxes-800 focus-visible:ring-taxes-100 text-white hover:text-white',
      revenues:
        'bg-revenues hover:bg-revenues-800 focus-visible:ring-revenues-100 text-white hover:text-white',
      red: 'bg-[#DC2626] hover:bg-red-500 focus-visible:ring-red-100 text-white hover:text-white',
      yellow:
        'bg-yellow-500 hover:bg-yellow-400 focus-visible:ring-yellow-100 text-white hover:text-white',
      main: 'bg-primary-700 hover:bg-primary-500 focus-visible:ring-primary-100 text-white hover:text-white',
      gray: 'bg-primary-50 hover:bg-primary-100 focus-visible:ring-primary-100 text-primary-800',
      'light-red': '',
      'light-expenses': '',
    },
    secondary: {
      expenses:
        'text-expenses border-expenses focus-visible:ring-expenses-800 hover:bg-expenses-100',
      bank: 'text-bank border-bank focus-visible:ring-bank-800 hover:bg-bank-100',
      primary:
        'text-primary border-primary focus-visible:ring-primary-800 hover:bg-primary-100',
      taxes:
        'text-taxes border-taxes focus-visible:ring-taxes-800  hover:bg-taxes-100',
      revenues:
        'text-revenues border-revenues focus-visible:ring-revenues-800 hover:bg-revenues-100 focus-visible:ring-revenues-400',
      red: 'text-[#DC2626] border-[#DC2626] focus-visible:ring-red-100 hover:bg-[#FEF2F2]',
      yellow:
        'text-yellow-500 focus-visible:ring-yellow-100 border-yellow-500 hover:bg-yellow-100',
      main: 'text-primary-700 border-primary-700 hover:bg-primary-500 hover:text-white',
      gray: 'hover:bg-primary-50 focus-visible:ring-primary-800',
      'light-red':
        'text-primary border-primary-100 hover:bg-[#FEF2F2] hover:text-[#DC2626] hover:border-[#FEF2F2] focus-visible:ring-red-100',
      'light-expenses':
        'text-primary border-primary-100 hover:bg-expenses-100 hover:text-expenses hover:border-expenses-100 focus-visible:ring-expenses-400',
    },
    light: {
      expenses:
        'text-expenses hover:border-expenses focus-visible:ring-expenses-400  hover:bg-white bg-expenses-100',
      bank: 'text-bank hover:border-bank focus-visible:ring-bank-400  hover:bg-white bg-bank-100',
      primary:
        'text-primary hover:border-primary focus-visible:ring-primary-400  hover:bg-white bg-primary-100',
      taxes:
        'text-taxes hover:border-taxes focus-visible:ring-taxes-400 hover:bg-white bg-taxes-100',
      revenues:
        'text-revenues hover:border-revenues focus-visible:ring-revenues-400  hover:bg-white bg-revenues-100',
      red: 'text-[#DC2626] hover:border-[#DC2626] focus-visible:ring-red-400  hover:bg-white bg-[#FEF2F2]',
      yellow:
        'text-yellow-500 hover:border-yellow-500 focus-visible:ring-yellow-400  hover:bg-white bg-yellow-100',
      main: 'text-primary hover:border-primary focus-visible:ring-primary-400  hover:bg-white bg-primary-100',
      gray: '',
      'light-red': '',
      'light-expenses': '',
    },
    transparent: {
      expenses:
        'text-expenses focus-visible:ring-expenses-400 hover:bg-expenses-100',
      bank: 'text-bank focus-visible:ring-bank-400 hover:bg-bank-100',
      primary:
        'text-primary focus-visible:ring-primary-400 hover:bg-primary-100',
      taxes: 'text-taxes focus-visible:ring-taxes-400 hover:bg-taxes-100',
      revenues:
        'text-revenues focus-visible:ring-revenues-400 hover:bg-revenues-100',
      red: 'text-[#DC2626] focus-visible:ring-red-400 hover:bg-[#FEF2F2]',
      yellow:
        'text-yellow-500 focus-visible:ring-yellow-400 hover:bg-yellow-100',
      main: 'text-primary focus-visible:ring-primary-400 hover:bg-primary-100',
      gray: '',
      'light-red': '',
      'light-expenses': '',
    },
    text: {
      bank: 'text-bank hover:text-bank-700',
      expenses: 'text-expenses hover:text-expenses-700',
      primary: 'text-primary hover:text-primary-800',
      taxes: 'text-taxes hover:text-taxes-700',
      revenues: 'text-revenues hover:text-revenues-700',
      red: 'text-[#DC2626] hover:text-red-500',
      yellow: 'text-yellow-500 hover:text-yellow-400',
      main: 'text-primary-700 hover:text-primary-500',
      gray: 'text-primary-400 hover:text-primary-500',
      'light-red': '',
      'light-expenses': '',
    },
  },
  size: {
    xs: 'p-1',
    sm: 'p-2',
  },
  icon: {
    size: {
      xs: 'w-3.5 h-3.5',
      sm: 'w-4 h-4',
      // md: 'w-4 h-4',
      // lg: 'w-5 h-5',
      // xl: 'w-5 h-5',
    },
  },
};

const Button: FC<ButtonComponentProps> = forwardRef(
  (props, ref): JSX.Element => {
    const {
      children,
      disabled,
      iconOnly,
      className: _className,
      onClick,
      asDefault,
      //--
      iconPlacement = 'left',
      loading,
      icon: PropsIcon,
      renderIcon,
      structure = 'primary',
      color,
      size = 'sm',
      tracingEvent,
      ariaDisabled,
      highlightID,
      extra,
      ...rest
    } = props;
    const worldColor = useWorldTheme();

    const tracingEvents = getTracingEvents(
      tracingEvent ? { onClick: tracingEvent } : {},
      { onClick },
    );
    const highlightAPI = useHighlight();

    const className = classNames(
      _className,
      highlightAPI.shouldHighlight(highlightID) && highlightAPI.className,
    );
    if (asDefault) {
      return (
        <button
          disabled={disabled || loading}
          {...(disabled || ariaDisabled
            ? { 'aria-disabled': true, tabIndex: -1 }
            : null)}
          className={className}
          onClick={
            disabled || ariaDisabled || loading
              ? undefined
              : tracingEvents.onClick
          }
          type="button"
          ref={ref}
          {...rest}
        >
          {children}
        </button>
      );
    }

    const Icon = PropsIcon ? (
      <PropsIcon
        aria-hidden
        className={classNames(theme.icon.size[size], {
          'mr-2': !!children && iconPlacement === 'left',
          'ml-2': !!children && iconPlacement === 'right',
        })}
      />
    ) : (
      renderIcon?.()
    );

    return (
      <button
        data-highlight-id={highlightID}
        type="button"
        // keep the key, the translation browser extension can change the dom content of the button children
        // react has issues with that when it tries to update the button if its loading
        // so we workaround it by changing the button key to make react destroy the old one and build a new one
        key={loading ? '_loading' : '_'}
        // if loading or disabled, button is disabled
        disabled={disabled || loading}
        {...(disabled || ariaDisabled || loading
          ? { 'aria-disabled': true, tabIndex: -1 }
          : null)}
        className={classNames(
          'aria-disabled:!cursor-not-allowed aria-disabled:!border-transparent transition-colors text-sm inline-flex items-center justify-center font-semibold rounded-10 focus:outline-none',
          theme.structure[structure],
          theme.colors[structure][color || worldColor],
          theme.size[size],
          {
            'px-0 !py-0': structure === 'text',
            'min-w-[5rem]': !iconOnly && structure !== 'text',
          },
          className,
        )}
        onClick={
          disabled || ariaDisabled || loading
            ? undefined
            : tracingEvents.onClick
        }
        ref={ref}
        {...rest}
      >
        {loading ? <Loader size={size} className={'mr-2'} /> : ''}
        {/* Show Icon on left if requested and not loading */}
        {Icon && !loading && iconPlacement === 'left' ? Icon : ''}
        {/* Show content */}
        {children}
        {/* Show Icon on right if requested and not loading */}
        {Icon && !loading && iconPlacement === 'right' ? Icon : ''}

        {extra}
      </button>
    );
  },
);

Button.displayName = 'Button';

export default Button;
